import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, TextField, Typography } from "@mui/material";
import { primaryButtonSx, ghostButtonSx, T, TYPE } from "../user/complaints/shared";
import { pm, errorMessage } from "./api";
import { usePm } from "./PmContext";
import RichEditor from "./RichEditor";
import { CustomFieldInput, LabelsInput, MultiSelect, PersonPicker, PrioritySelect, Select, TypeSelect, applicableFields, fieldSx } from "./inputs";

/**
 * Create issue. `project` may be omitted (global "Create" button) — then a
 * project picker appears. `defaults` pre-fills (sprintId, parentId, typeKey…).
 */
const CreateIssueDialog = ({ open, onClose, project: fixedProject, defaults = {}, onCreated }) => {
  const ctx = usePm();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(fixedProject?._id || null);
  const [project, setProject] = useState(fixedProject || null);
  const [refs, setRefs] = useState({ sprints: [], versions: [], components: [], epics: [] });
  const [form, setForm] = useState({});
  const [more, setMore] = useState(false);
  const [another, setAnother] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setError("");
    const init = { summary: "", description: "", priorityId: null, assigneeId: null, labels: [], componentIds: [], fixVersionIds: [], customFields: {}, storyPoints: "", dueDate: "", startDate: "", originalEstimate: "", ...defaults };
    setForm(init);
    if (!fixedProject) pm.projects().then(setProjects).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || !projectId) return;
    let alive = true;
    (async () => {
      const p = fixedProject && String(fixedProject._id) === String(projectId) ? fixedProject : await pm.project(projectId);
      const [sprints, versions, components, epics] = await Promise.all([
        pm.sprints(projectId, { status: "active,future" }).catch(() => []), pm.versions(projectId).catch(() => []),
        pm.components(projectId).catch(() => []), pm.epics(projectId).catch(() => []),
      ]);
      if (!alive) return;
      setProject(p);
      setRefs({ sprints, versions: versions.filter((v) => v.status === "unreleased"), components, epics });
      setForm((f) => {
        const typeId = f.typeId || (f.typeKey && ctx.typeByKey.get(f.typeKey)?._id) || p.issueTypeIds.map(String).find((id) => ctx.typeById.get(id)?.key === "task") || p.issueTypeIds[0];
        return { ...f, typeId };
      });
    })().catch((err) => setError(errorMessage(err)));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, projectId]);

  const type = ctx.typeById.get(String(form.typeId));
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const fields = useMemo(() => applicableFields(ctx.customFields, projectId, form.typeId), [ctx.customFields, projectId, form.typeId]);

  const parentOptions = useMemo(() => {
    if (!type) return [];
    return (type.allowedParentTypeIds || []).length ? (type.level === 2 ? null : refs.epics) : [];
  }, [type, refs.epics]);
  const [parentSearch, setParentSearch] = useState([]);
  useEffect(() => {
    if (!open || !project || type?.level !== 2) return;
    pm.projectIssues(project._id, { all: [{ field: "type", op: "in", value: (type.allowedParentTypeIds || []).map(String) }, { field: "statusCategory", op: "neq", value: "done" }] }, { limit: 200, sort: "updated", order: "desc" })
      .then((r) => setParentSearch(r.data)).catch(() => setParentSearch([]));
  }, [open, project, type]);

  const submit = async () => {
    setSaving(true);
    setError("");
    try {
      const body = {
        typeId: form.typeId, summary: form.summary, description: form.description, priorityId: form.priorityId || undefined, assigneeId: form.assigneeId || undefined,
        labels: form.labels, componentIds: form.componentIds, fixVersionIds: form.fixVersionIds, sprintId: form.sprintId || undefined,
        parentId: type?.level === 2 ? form.parentId : undefined, epicId: type?.level === 1 ? form.parentId || form.epicId : undefined,
        storyPoints: form.storyPoints === "" ? undefined : Number(form.storyPoints), dueDate: form.dueDate || undefined, startDate: form.startDate || undefined,
        originalEstimate: form.originalEstimate || undefined, customFields: form.customFields,
      };
      if (type?.level === 1 && body.epicId) {
        body.parentId = body.epicId;
        delete body.epicId;
      }
      const issue = await pm.createIssue(project._id, body);
      ctx.notify(`${issue.key} created`);
      onCreated?.(issue);
      if (another) setForm((f) => ({ ...f, summary: "", description: "" }));
      else {
        onClose();
        if (!onCreated) navigate(`/projects/${project.key}/issues/${issue.key}`);
      }
    } catch (err) {
      setError(errorMessage(err, "Could not create the issue"));
    } finally {
      setSaving(false);
    }
  };

  const upload = async (files) => (project ? pm.uploadFiles(project._id, files, "Images") : []);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: T.radiusCard } }}>
      <DialogTitle sx={{ fontWeight: 700 }}>Create issue{project ? ` in ${project.key}` : ""}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          {!fixedProject && (
            <Grid item xs={12} sm={6}>
              <Select label="Project" value={projectId} onChange={setProjectId} options={projects.map((p) => ({ value: p._id, label: `${p.key} · ${p.name}` }))} />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            {project && <TypeSelect value={form.typeId} onChange={set("typeId")} typeIds={project.issueTypeIds} />}
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth size="small" label="Summary *" value={form.summary || ""} onChange={(e) => set("summary")(e.target.value)} autoFocus sx={fieldSx} inputProps={{ maxLength: 500 }} />
          </Grid>
          {type?.level === 2 && (
            <Grid item xs={12} sm={6}>
              <Select label="Parent *" value={form.parentId} onChange={set("parentId")} options={parentSearch.map((i) => ({ value: i._id, label: `${i.key} · ${i.summary}` }))} />
            </Grid>
          )}
          {type?.level === 1 && parentOptions?.length > 0 && (
            <Grid item xs={12} sm={6}>
              <Select label="Epic" value={form.parentId} onChange={set("parentId")} allowEmpty options={parentOptions.map((e) => ({ value: e._id, label: `${e.key} · ${e.summary}` }))} />
            </Grid>
          )}
          <Grid item xs={12}>
            <Typography sx={{ fontSize: TYPE.micro, color: T.inkSecondary, mb: 0.5 }}>Description</Typography>
            {open && <RichEditor value={form.description} onChange={set("description")} onUpload={upload} mentionRoles={ctx.projectRoles} />}
          </Grid>
          <Grid item xs={12} sm={4}><PersonPicker value={form.assigneeId} onChange={set("assigneeId")} /></Grid>
          <Grid item xs={12} sm={4}><PrioritySelect value={form.priorityId || ctx.priorities.find((p) => p.isDefault)?._id} onChange={set("priorityId")} /></Grid>
          {project?.sprintsEnabled && (
            <Grid item xs={12} sm={4}>
              <Select label="Sprint" value={form.sprintId} onChange={set("sprintId")} allowEmpty emptyLabel="Backlog" options={refs.sprints.map((s) => ({ value: s._id, label: `${s.name}${s.status === "active" ? " (active)" : ""}` }))} />
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControlLabel control={<Checkbox size="small" checked={more} onChange={(e) => setMore(e.target.checked)} />} label={<Typography sx={{ fontSize: TYPE.small }}>More fields</Typography>} />
          </Grid>
          {more && (
            <>
              <Grid item xs={12} sm={6}><LabelsInput value={form.labels} onChange={set("labels")} /></Grid>
              <Grid item xs={12} sm={6}><MultiSelect label="Components" value={form.componentIds} onChange={set("componentIds")} options={refs.components.map((c) => ({ value: c._id, label: c.name }))} /></Grid>
              <Grid item xs={12} sm={6}><MultiSelect label="Fix versions" value={form.fixVersionIds} onChange={set("fixVersionIds")} options={refs.versions.map((v) => ({ value: v._id, label: v.name }))} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="number" label={project?.estimation === "hours" ? "Story points (optional)" : "Story points"} value={form.storyPoints} onChange={(e) => set("storyPoints")(e.target.value)} sx={fieldSx} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" label="Estimate" placeholder="2h 30m" value={form.originalEstimate} onChange={(e) => set("originalEstimate")(e.target.value)} sx={fieldSx} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Start date" InputLabelProps={{ shrink: true }} value={form.startDate} onChange={(e) => set("startDate")(e.target.value)} sx={fieldSx} /></Grid>
              <Grid item xs={6} sm={3}><TextField fullWidth size="small" type="date" label="Due date" InputLabelProps={{ shrink: true }} value={form.dueDate} onChange={(e) => set("dueDate")(e.target.value)} sx={fieldSx} /></Grid>
            </>
          )}
          {fields.length > 0 && (
            <Grid item xs={12}>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                {fields.map((f) => (
                  <CustomFieldInput key={f._id} field={f} value={form.customFields?.[f.key]} onChange={(v) => setForm((x) => ({ ...x, customFields: { ...x.customFields, [f.key]: v } }))}
                    refs={{ teams: ctx.teams, sprints: refs.sprints, versions: refs.versions, projects }} />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <FormControlLabel control={<Checkbox size="small" checked={another} onChange={(e) => setAnother(e.target.checked)} />} label={<Typography sx={{ fontSize: TYPE.small }}>Create another</Typography>} sx={{ mr: "auto" }} />
        <Button variant="outlined" sx={ghostButtonSx} onClick={onClose}>Cancel</Button>
        <Button variant="contained" sx={primaryButtonSx} disabled={saving || !project || !form.summary?.trim() || !form.typeId} onClick={submit}>{saving ? "Creating…" : "Create"}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateIssueDialog;
